import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { MoneyInput as SparkMoneyInput } from 'react-spark';
import {
  withValidationOutputDefaultProps,
  withValidationOutputPropTypes,
  sharedFormInputPropTypes,
  sharedFormInputDefaultProps
} from '../../forms/hocs/prop-types';
import { withValidation } from '../../forms/hocs/WithValidation';
import './NovaMoneyInput.scss';

const createIncomeValidation = (minAmount = 24000, messages = {}) => ({
  validateOn: ["blur"],
  required: true,
  checkValidity: value => {
    const cleanedValue = NovaMoneyInput.cleanValue(value);
    const amount = parseInt(cleanedValue);

    if (!SparkMoneyInput.pattern.test(value)) {
      return {
        isValid: false
      };
    }

    if (amount < minAmount) {
      return {
        isValid: false,
        inputAttr: "minlength"
      };
    }

    return {
      isValid: true
    };
  },
  message: {
    default: messages.default || "Please enter your annual gross income",
    minlength: messages.minAmount || `Your income is below $${minAmount.toLocaleString()}`
  }
});

class NovaMoneyInput extends Component {
  static cleanValue(value = '') {
    return String(value).replace(/[^\d\.]/g, '');
  }

  static defaultProps = {
    ...withValidationOutputDefaultProps,
    ...sharedFormInputDefaultProps,
    value: '',
    label: '',
    placeholder: '$0',
    className: '',
    enableIncomeValidation: false,
    minAmount: 24000,
    incomeValidationMessages: {},
    maxLength: 8,
    hint: ''
  };

  static propTypes = {
    ...withValidationOutputPropTypes,
    ...sharedFormInputPropTypes,
    value: PropTypes.string,
    label: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    className: PropTypes.string,
    enableIncomeValidation: PropTypes.bool,
    minAmount: PropTypes.number,
    incomeValidationMessages: PropTypes.shape({
      default: PropTypes.string,
      minAmount: PropTypes.string
    }),
    maxLength: PropTypes.number,
    hint: PropTypes.string
  };

  constructor(props) {
    super(props);
    this.state = {
      focused: false,
      hasValue: Boolean(props.value)
    };
    this.inputRef = React.createRef();
  }

  componentDidMount() {
    this.props.context.registerFormElement({
      value: this.props.value,
      name: this.props.name,
      valid: this.props.validateOnRegisterUpdate.apply(this),
      validate: this.props.handleValidation
    });
  }

  componentDidUpdate(prevProps) {
    if (this.props.forceUpdate) {
      this.props.context.updateFormElement({
        value: this.props.value,
        name: this.props.name,
        valid: this.props.validateOnRegisterUpdate.apply(this)
      });
    }

    // Update hasValue state when value changes
    if (prevProps.value !== this.props.value) {
      this.setState({
        hasValue: Boolean(this.props.value)
      });
    }
  }

  handleFocus = (event) => {
    this.setState({ focused: true });
    if (this.props.onFocus) {
      this.props.onFocus(event);
    }
  };

  handleBlur = (event) => {
    this.setState({ focused: false });
    this.props.handleBlur(event, this.props.context.handleChange);
  };

  handleChange = (event) => {
    const { value } = event.target;
    const cleanValue = NovaMoneyInput.cleanValue(value);
    
    if (this.props.maxLength && cleanValue.length > this.props.maxLength) {
      return; // Prevent change if exceeds maxLength
    }
    
    this.setState({ hasValue: Boolean(value) });
    this.props.handleChange(event, this.props.context);
  };

  formatValue = (value) => {
    if (!value) return '';
    
    // Remove non-numeric characters except decimal point
    let cleanValue = value.replace(/[^\d\.]/g, '');
    
    // Handle decimal places
    const parts = cleanValue.split('.');
    if (parts.length > 2) {
      cleanValue = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Add commas for thousands
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const decimalPart = parts[1] ? '.' + parts[1].slice(0, 2) : '';
    
    return '$' + integerPart + decimalPart;
  };

  render() {
    const {
      id,
      name,
      value,
      label,
      placeholder,
      disabled,
      errorMessage,
      className,
      context,
      enableIncomeValidation,
      minAmount,
      incomeValidationMessages,
      validation,
      hint
    } = this.props;

    const { focused, hasValue } = this.state;
    
    const resolvedValue = (context && context.elements && context.elements[name] && context.elements[name].value) || value;
    const displayValue = this.formatValue(resolvedValue);
    
    const containerClasses = [
      'nova-money-input',
      focused ? 'nova-money-input--focused' : '',
      hasValue ? 'nova-money-input--filled' : '',
      errorMessage ? 'nova-money-input--error' : '',
      disabled ? 'nova-money-input--disabled' : '',
      className
    ].filter(Boolean).join(' ');

    const ariaDescribedBy = [
      hint ? `${id}-hint` : null,
      errorMessage ? `${id}-error` : null
    ].filter(Boolean).join(' ') || undefined;

    return (
      <div className={containerClasses}>
        <div className="nova-money-input__field-wrapper">
          <label htmlFor={id} className="nova-money-input__label">
            {label}
          </label>
          <input
            ref={this.inputRef}
            id={id}
            name={name}
            type="text"
            inputMode="numeric"
            value={displayValue}
            placeholder={placeholder}
            disabled={disabled}
            className="nova-money-input__input"
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            onChange={this.handleChange}
            aria-describedby={ariaDescribedBy}
            aria-invalid={errorMessage ? 'true' : 'false'}
          />
        </div>
        {(errorMessage || hint) && (
          <div className="nova-money-input__description">
            {errorMessage && (
              <span className="nova-money-input__error" id={`${id}-error`} role="alert">
                {errorMessage}
              </span>
            )}
            {hint && (
              <span className="nova-money-input__hint" id={`${id}-hint`}>
                {hint}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
}

// Apply withValidation HOC to the base component first
const ValidatedNovaMoneyInput = withValidation(NovaMoneyInput);

class NovaMoneyInputWithValidation extends Component {
  static cleanValue = NovaMoneyInput.cleanValue;

  static defaultProps = {
    enableIncomeValidation: false,
    minAmount: 24000,
    incomeValidationMessages: {},
    maxLength: 8
  };

  static propTypes = {
    enableIncomeValidation: PropTypes.bool,
    minAmount: PropTypes.number,
    incomeValidationMessages: PropTypes.shape({
      default: PropTypes.string,
      minAmount: PropTypes.string
    }),
    maxLength: PropTypes.number
  };

  render() {
    const {
      enableIncomeValidation,
      minAmount,
      incomeValidationMessages,
      validation,
      ...otherProps
    } = this.props;

    const effectiveValidation = enableIncomeValidation 
      ? createIncomeValidation(minAmount, incomeValidationMessages)
      : validation;

    return (
      <ValidatedNovaMoneyInput
        {...otherProps}
        validation={effectiveValidation}
      />
    );
  }
}

// Export the enhanced component
export default NovaMoneyInputWithValidation;
